import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useKmClient } from '../../lib/useKmClient.js';
import { useSelfAssessmentSession } from './hooks/useSelfAssessmentSession.js';
import MessageBubble from './components/MessageBubble.jsx';
import TypingIndicator from './components/TypingIndicator.jsx';
import ConsentCard from './components/ConsentCard.jsx';
import ChatComposer from './components/ChatComposer.jsx';
import QuickReplies from './components/QuickReplies.jsx';
import GrigliaScelte from './components/GrigliaScelte.jsx';
import GrigliaVoti from './components/GrigliaVoti.jsx';
import DemoBadge from '../../components/DemoBadge.jsx';
import '../../styles/self-assessment.css';

export default function SelfAssessmentPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const kmClient = useKmClient();
  const scrollRef = useRef(null);

  const {
    phase,
    messages,
    pendingText,
    setPendingText,
    isSending,
    fatalMessage,
    progresso,
    sendMessage,
    consent,
  } = useSelfAssessmentSession(kmClient, { token });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isSending]);

  // Pulsanti di risposta rapida dell'ultimo messaggio (tolti il 23/09,
  // rimessi il 13/10). Il backend li manda solo dove esistono davvero:
  // «Continuiamo / Chiudiamo qui» del set minimo, conferma dei recapiti e
  // del riepilogo, scelte chiuse con le opzioni a DB. Senza, al collaudo
  // del 13/10 non c'era modo di chiudere il SA al set minimo. Il campo di
  // testo resta sempre disponibile.
  const lastBotMessage = [...messages].reverse().find((m) => m.role === 'bot' && !m.isError);
  const quickReply = phase === 'chatting' ? lastBotMessage?.quickReply : null;
  // Autovalutazione delle competenze: la griglia 1–5 a clic sostituisce i pulsanti voto per voto.
  const griglia = phase === 'chatting' ? lastBotMessage?.griglia : null;
  const selezione = phase === 'chatting' ? lastBotMessage?.selezione : null;

  if (phase === 'invalid_token') {
    return (
      <div className="sa-page km-scope">
        <div className="sa-fullscreen-state">
          <div>
            <h1 className="section-title">Link non valido</h1>
            <p style={{ color: 'var(--color-ink-muted)' }}>
              Questo link è scaduto oppure il Self-Assessment è già stato
              concluso. Contatta il tuo referente HR per ricevere un nuovo invito.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'fatal_error') {
    return (
      <div className="sa-page km-scope">
        <div className="sa-fullscreen-state">
          <div>
            <h1 className="section-title">Servizio non disponibile</h1>
            <p style={{ color: 'var(--color-ink-muted)' }}>{fatalMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="sa-page km-scope">
        <div className="sa-fullscreen-state">
          <div>
            <h1 className="section-title">Sessione conclusa</h1>
            <p style={{ color: 'var(--color-ink-muted)' }}>
              La conversazione non è più attiva. Se ti serve riprenderla,
              contatta HR: potrà generarti un nuovo link mantenendo le
              risposte già fornite.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sa-page km-scope">
      <header className="sa-header">
        <span className="sa-header-title">Self-Assessment ISA</span>
        <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <DemoBadge />
          {/* Richiesto il 23/09 — il dato era già nella risposta reale
              (meta.progresso), mai letto finora. "Domanda X di Y":
              risposte_confermate + 1 (la corrente, non ancora confermata)
              su domande_attive. Non mostrato durante 'completed' (il
              conteggio non è più rilevante a questionario concluso), né
              se il dato non è ancora arrivato (primo turno). */}
          {phase === 'chatting' && progresso && (
            <span style={{ fontSize: 12.5, color: 'var(--color-ink-muted)' }}>
              Domanda {Math.min(progresso.risposte_confermate + 1, progresso.domande_attive)} di {progresso.domande_attive}
            </span>
          )}
          {phase === 'completed' && <span className="badge badge-success">Completato</span>}
        </span>
      </header>

      <div className="sa-chat-scroll" ref={scrollRef}>
        <div className="sa-chat-column">
          {phase === 'loading' && <TypingIndicator />}

          {messages.map((m) => (
            <MessageBubble key={m.id} role={m.role} text={m.text} isError={m.isError} />
          ))}

          {phase === 'consent' && <ConsentCard onConsent={consent} disabled={isSending} />}

          {selezione && !isSending && (
            <GrigliaScelte
              key={lastBotMessage.id}
              griglia={selezione}
              disabled={isSending}
              onInvia={(valori, riepilogo) => sendMessage(riepilogo, { replyId: selezione.invio + JSON.stringify(valori) })}
            />
          )}

          {griglia && !isSending && (
            <GrigliaVoti
              key={lastBotMessage?.id}
              griglia={griglia}
              disabled={isSending}
              onInvia={(voti, riepilogo) => sendMessage(riepilogo, { replyId: griglia.invio + JSON.stringify(voti) })}
            />
          )}

          {quickReply && !griglia && !isSending && (
            <QuickReplies
              options={quickReply}
              disabled={isSending}
              onSelect={(opt) => sendMessage(opt.label, { replyId: opt.id })}
            />
          )}

          {isSending && <TypingIndicator />}
        </div>
      </div>

      {(phase === 'chatting') && (
        <ChatComposer
          value={pendingText}
          onChange={setPendingText}
          onSend={() => sendMessage(pendingText)}
          disabled={isSending}
        />
      )}

      {phase === 'completed' && (
        <div className="sa-fullscreen-state" style={{ flex: 'none', padding: 'var(--space-6)' }}>
          <p style={{ color: 'var(--color-ink-muted)' }}>
            Grazie per aver completato il Self-Assessment. Puoi chiudere
            questa pagina.
          </p>
        </div>
      )}
    </div>
  );
}
